'use client'

import { useRef } from 'react'

import { reachInk, useRowGeometry } from '@/features/landing/method/reach'

// Spec §3.1: the four frames as a running index on one hairline — glyph
// and frame number, tabular, muted except the frames reached; one ink
// underline that stretches toward the next name and springs across when
// the step turns. Nothing else moves. The row's geometry is read once
// (and on resize), not on every scrubbed frame; the ink is arithmetic.
export function IndexStepper({
    frames,
    i,
    t,
}: {
    frames: Array<{ character: string; frame: number }>
    i: number
    t: number
}) {
    const ref = useRef<HTMLDivElement>(null)
    const row = useRowGeometry(ref)
    // null until measured, so the server HTML paints no full-width line
    const ink = row ? reachInk(row.items, i, t, row.width) : null
    return (
        <div ref={ref} className="relative border-b border-rule pb-2">
            <ol
                className="flex gap-6 font-serif text-lp-caption tabular-nums"
                aria-label="Frames"
            >
                {frames.map((f, k) => (
                    <li
                        key={f.frame}
                        aria-current={k === i ? 'step' : undefined}
                        className={
                            k <= i
                                ? 'flex items-baseline gap-1.5 text-ink transition-colors duration-300'
                                : 'flex items-baseline gap-1.5 text-muted-fg transition-colors duration-300'
                        }
                    >
                        <span className="font-mincho text-lp-gloss">
                            {f.character}
                        </span>
                        <span className="tracking-lp-16 uppercase">
                            {f.frame}
                        </span>
                    </li>
                ))}
            </ol>
            {ink ? (
                <span
                    aria-hidden
                    className="absolute -bottom-px h-0.5 bg-ink transition-[left] duration-[600ms] ease-[cubic-bezier(.22,1,.36,1)] motion-reduce:transition-none"
                    style={{ left: ink.left, right: ink.right }}
                />
            ) : null}
        </div>
    )
}
